// 261010 UI-icons — bảng trạng thái → {biểu tượng, màu, nhãn} của khung 8 bước. THUẦN (không antd / router / @ant-design/icons): chỉ trả TÊN biểu tượng; WfIcon.tsx đổi tên thành component.
// Mọi chữ trạng thái đang dùng ở bảng (STATE_META, stepBadgeOf, bộ lọc, hàng video, cờ hàng, trạng thái plan) đều có luật ở đây; chữ gốc đi vào Tooltip / aria-label, KHÔNG hiện ra màn.
import type { StateTone } from './workflowBoardUtils';

export type IconName =
  | 'check' | 'sync' | 'clock' | 'loading' | 'close' | 'pause' | 'eye' | 'warning' | 'picture' | 'message' | 'list' | 'user' | 'team' | 'userAdd'
  | 'lock' | 'unlock' | 'video' | 'scissor' | 'download' | 'edit' | 'right' | 'down' | 'up' | 'expand' | 'collapse' | 'reload' | 'play' | 'stop'
  | 'history' | 'font' | 'smile' | 'tool' | 'all' | 'back' | 'info'
  | 'man' | 'woman' | 'safety' | 'question' | 'ellipsis' | 'upload' | 'plus' | 'bulb' | 'star' | 'starFilled';

/** Cả danh sách tên — test dùng để chắc `WfIcon` phủ đủ. */
export const ICON_NAMES: IconName[] = [
  'check', 'sync', 'clock', 'loading', 'close', 'pause', 'eye', 'warning', 'picture', 'message', 'list', 'user', 'team', 'userAdd',
  'lock', 'unlock', 'video', 'scissor', 'download', 'edit', 'right', 'down', 'up', 'expand', 'collapse', 'reload', 'play', 'stop',
  'history', 'font', 'smile', 'tool', 'all', 'back', 'info',
  'man', 'woman', 'safety', 'question', 'ellipsis', 'upload', 'plus', 'bulb', 'star', 'starFilled',
];

export interface StatusView {
  icon: IconName;
  tone: StateTone;
  /** Chữ gốc (đi vào Tooltip + aria-label). */
  label: string;
  /** Số / tỉ lệ đi kèm biểu tượng ("3", "2/9"); null = chỉ biểu tượng. */
  value: string | null;
  /** false = rơi về biểu tượng theo màu (chữ lạ) — test không cho phép với chữ đang dùng. */
  known: boolean;
}

type Rule = { re: RegExp; icon: IconName; value?: (m: RegExpExecArray) => string | null };
const RULES: Rule[] = [
  { re: /^đang chạy(?: (\d+(?:\/\d+)?))?$/, icon: 'loading', value: (m) => m[1] ?? null },
  { re: /^AI đang (?:chấm|sửa)(?: (\d+\/\d+))?$/, icon: 'loading', value: (m) => m[1] ?? null }, // P9Q: AI chấm/sửa = đang chờ máy (xoay), không phải lỗi
  { re: /^(?:(\d+) )?lỗi$/, icon: 'close', value: (m) => m[1] ?? null },
  { re: /^(?:(\d+) )?cũ$/, icon: 'sync', value: (m) => m[1] ?? null },
  { re: /^dừng · còn (\d+)$/, icon: 'pause', value: (m) => m[1] },
  { re: /^đã duyệt$/, icon: 'check' },
  { re: /^chờ duyệt$/, icon: 'eye' },
  { re: /^chờ chọn$/, icon: 'star' }, // video-ui: có bản clip xong nhưng chưa chọn bản nào
  { re: /^cần duyệt lại$/, icon: 'sync' },
  { re: /^(?:xong|có kết quả)$/, icon: 'check' },
  { re: /^(\d+\/\d+)$/, icon: 'check', value: (m) => m[1] },
  { re: /^(?:chưa tới|chưa có)$/, icon: 'clock' },
  { re: /^thiếu$/, icon: 'clock' },
];

const FALLBACK_BY_TONE: Record<StateTone, IconName> = { ok: 'check', ready: 'check', doing: 'loading', bad: 'close', warn: 'warning', todo: 'clock' };

/** Chip trạng thái (chữ `stepBadgeOf` / `STATE_META.label` + tông) → biểu tượng + màu. */
export function statusIconOf(text: string, tone: StateTone): StatusView {
  const t = String(text ?? '').trim();
  for (const r of RULES) {
    const m = r.re.exec(t);
    if (m) return { icon: r.icon, tone, label: t, value: r.value ? r.value(m) : null, known: true };
  }
  return { icon: FALLBACK_BY_TONE[tone] ?? 'info', tone, label: t, value: null, known: false };
}

// ── bộ lọc trạng thái ──
/** Mỗi bộ lọc của hàng công cụ → biểu tượng + tông. `ATTN` ("Cần xử lý") giữ chữ, các bộ lọc còn lại chỉ biểu tượng + số. */
export function filterIconOf(key: string): { icon: IconName; tone: StateTone } {
  switch (key) {
    case 'ATTN': return { icon: 'warning', tone: 'warn' };
    case 'ERROR': return { icon: 'close', tone: 'bad' };
    case 'STALE': return { icon: 'sync', tone: 'warn' };
    case 'RUNNING': return { icon: 'loading', tone: 'doing' };
    case 'NOT_READY': return { icon: 'clock', tone: 'todo' };
    case 'DONE': return { icon: 'check', tone: 'ok' };
    default: return { icon: 'all', tone: 'todo' };
  }
}

// ── chữ phụ của hàng video ──
export interface RowChipView { icon: IconName; value: string | null; label: string; tone?: StateTone }

/** Chữ meta ("00:08", "3 thoại", "2 giai đoạn", "12 s", "340 ký tự", "Tự nhiên", tên nhân vật ở bước Đạo diễn) → biểu tượng + số. Không nhận ra ⇒ null (giữ chữ). */
export function metaChipOf(text: string, step?: string): RowChipView | null {
  const t = String(text ?? '').trim();
  let m: RegExpExecArray | null;
  if ((m = /^(\d{1,3}):(\d{2})$/.exec(t))) return { icon: 'clock', value: t, label: `Thời lượng ${t}` };
  if ((m = /^(\d+) thoại$/.exec(t))) return { icon: 'message', value: m[1], label: t };
  if ((m = /^(\d+) giai đoạn$/.exec(t))) return { icon: 'list', value: m[1], label: t };
  if ((m = /^([\d.,]+) s$/.exec(t))) return { icon: 'clock', value: `${m[1]}s`, label: `Tổng thời lượng shot ${t}` };
  if ((m = /^(\d+) ký tự$/.exec(t))) return { icon: 'font', value: m[1], label: t };
  if (t === 'Tự nhiên') return { icon: 'smile', value: null, label: 'Kiểu prompt: Tự nhiên' };
  if (t === 'Kỹ thuật') return { icon: 'tool', value: null, label: 'Kiểu prompt: Kỹ thuật' };
  if (step === 'DIRECTING' && t) return { icon: 'team', value: t, label: `Nhân vật trong các shot: ${t}` };
  return null;
}

const cap1 = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
/** Cờ của hàng video ("2 cảnh báo", "lỗi chạy", "cần chia", "thiếu 1 sơ đồ", "2 khung lệch", "1 cảnh báo prompt", "lệch kiểu") → biểu tượng + số. Không nhận ra ⇒ null (giữ chữ). */
export function flagChipOf(text: string): RowChipView | null {
  const t = String(text ?? '').trim();
  let m: RegExpExecArray | null;
  if ((m = /^(\d+) cảnh báo(?: prompt)?$/.exec(t))) return { icon: 'warning', value: m[1], label: t, tone: 'warn' };
  if (t === 'lỗi chạy') return { icon: 'close', value: null, label: t, tone: 'bad' };
  if (t === 'cần chia') return { icon: 'scissor', value: null, label: 'Video quá dài — máy sẽ tự chia ở bước Đạo diễn', tone: 'warn' };
  if ((m = /^thiếu (\d+) sơ đồ$/.exec(t))) return { icon: 'picture', value: m[1], label: t, tone: 'warn' };
  if ((m = /^(\d+) khung lệch$/.exec(t))) return { icon: 'warning', value: m[1], label: t, tone: 'warn' };
  if (t === 'lệch kiểu') return { icon: 'warning', value: null, label: t, tone: 'warn' };
  // UI-errbox: nhãn kết cục / nguyên nhân của cờ bền (flagInfoOf) — CHỈ biểu tượng, nhãn đi vào Tooltip (không chip chữ lặp lại trạng thái hàng).
  if (t === 'chưa rõ kết quả') return { icon: 'question', value: null, label: 'Chưa rõ kết quả — cần đối soát', tone: 'warn' };
  if (/^chưa chạy(?: \(.+\))?$/.test(t)) return { icon: 'clock', value: null, label: `Chưa chạy${t.length > 9 ? t.slice(9) : ''}`, tone: 'todo' };
  if (t === 'bị chặn') return { icon: 'lock', value: null, label: 'Thiếu đầu vào để làm — bổ sung rồi chạy lại', tone: 'warn' };
  if (t === 'chờ bước trước') return { icon: 'lock', value: null, label: 'Chờ bước trước có kết quả', tone: 'warn' };
  if (t === 'cần đối soát') return { icon: 'question', value: null, label: 'Cần đối soát — máy không tự tạo lại', tone: 'warn' };
  if (['chưa có prompt', 'prompt lệch kiểu', 'thiếu thời lượng', 'do AI ngoài làm', 'thiếu đầu vào', 'thiếu cấu hình', 'cấu hình hỏng', 'clip đang tạo', 'chưa chọn clip'].includes(t)) return { icon: 'warning', value: null, label: cap1(t), tone: 'warn' };
  return null;
}

/** Dòng tóm tắt cảnh `sceneLineOf` ("Cảnh 2 · 3 video · 1 lỗi · 2 thiếu · 1 cũ") → các chip biểu tượng + số (bỏ phần «Cảnh N»). Phần lạ ⇒ chip 'info' mang nguyên chữ ở Tooltip. */
export function sceneLineChipsOf(line: string | null | undefined): RowChipView[] {
  const out: RowChipView[] = [];
  for (const part of String(line ?? '').split(' · ').map((x) => x.trim()).filter(Boolean)) {
    let m: RegExpExecArray | null;
    if (/^Cảnh \d+$/.test(part)) continue;
    if ((m = /^(\d+) video$/.exec(part))) out.push({ icon: 'video', value: m[1], label: part });
    else if ((m = /^(\d+) lỗi$/.exec(part))) out.push({ icon: 'close', value: m[1], label: part, tone: 'bad' });
    else if ((m = /^(\d+) thiếu$/.exec(part))) out.push({ icon: 'clock', value: m[1], label: part, tone: 'todo' });
    else if ((m = /^(\d+) cũ$/.exec(part))) out.push({ icon: 'sync', value: m[1], label: part, tone: 'warn' });
    else out.push({ icon: 'info', value: null, label: part });
  }
  return out;
}

// ── nút hành động ──
/** Biểu tượng đứng trước nhãn nút chính (chỉ thêm, nhãn chữ giữ nguyên). */
export function primaryIconOf(key: string): IconName | null {
  switch (key) {
    case 'STOP': return 'stop';
    case 'RUN_SCRIPT': case 'RUN_MISSING': return 'play';
    case 'APPROVE_SCRIPT': case 'APPROVE_ASSETS': case 'APPROVE_STEP': return 'check';
    case 'OPEN_FAILED': return 'warning';
    case 'DOWNLOAD_CAPCUT': return 'download';
    default: return null;
  }
}

/** Biểu tượng của nút ở đầu mục bước (`SecondaryAction.key`). */
export function headActionIconOf(key: string): IconName {
  switch (key) {
    case 'APPROVE': return 'check';
    case 'RUN_FAILED': case 'RERUN_STAGE1': return 'reload';
    default: return 'play'; // RUN_STAGE1 · RUN_BATCH · RUN_MISSING
  }
}

// ── trạng thái plan ở thẻ danh sách ──
export function planStatusIconOf(status: string): { icon: IconName; tone: StateTone } {
  switch (status) {
    case 'DRAFT': return { icon: 'edit', tone: 'todo' };
    case 'PLAN_APPROVED': return { icon: 'check', tone: 'ready' };
    case 'FRAMES_PENDING': return { icon: 'loading', tone: 'doing' };
    case 'FRAMES_APPROVED': return { icon: 'picture', tone: 'ready' };
    case 'VIDEO_PENDING': return { icon: 'loading', tone: 'doing' };
    case 'VIDEO_APPROVED': return { icon: 'video', tone: 'ready' };
    case 'ASSEMBLED': return { icon: 'check', tone: 'ok' };
    default: return { icon: 'info', tone: 'todo' };
  }
}
